import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Chip,
    Grid,
    IconButton,
    List,
    ListItem,
    makeStyles,
    Menu,
    MenuItem,
} from "@material-ui/core"
import { Add, Block, DragIndicator, ExpandMore, MoreVert } from "@material-ui/icons"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import useAppStore from "../../../stores/AppStore"
import { ehProdutoAdicionais, useCardapioContext } from "./CardapioContext"
import DialogConfirmacao from "./DialogConfirmacao"
import DialogCopiarCategoria from "./DialogCopiarCategoria"
import ItemProduto from "./ItemProduto"
import { produtoCasaBusca } from "../utils/buscaCardapio"
import { TextoEditavel } from "./TextoEditavel"

const ITEM_HEIGHT = 48

const useStyles = makeStyles(() => ({
    container: {
        width: "100%",
    },
    accordion: {
        width: "100%",
        borderRadius: "16px !important",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        marginBottom: "16px",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: "0 0 16px 0" },
    },
    accordionSummary: {
        backgroundColor: "#f8f9fa",
        "&.Mui-expanded": {
            borderBottom: "2px solid #e9ecef",
        },
    },
    // Destaque enquanto a categoria está sendo arrastada (reordenação).
    accordionArrastando: {
        boxShadow: "0 0 0 2px rgba(76,175,80,0.5) !important",
        opacity: 0.9,
    },
    dragHandle: {
        cursor: "grab",
        color: "#9e9e9e",
        "&:active": { cursor: "grabbing" },
    },
    tituloCategoria: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    tituloInativo: {
        color: "#9e9e9e",
        textDecoration: "line-through",
    },
    summaryInativo: {
        backgroundColor: "#fbeaea",
        borderLeft: "4px solid #DC4C64",
    },
    chipInativa: {
        marginLeft: 10,
        backgroundColor: "#fff",
        color: "#DC4C64",
        border: "2px solid #DC4C64",
        fontWeight: 700,
        height: 24,
        "& .MuiChip-label": { color: "#DC4C64" },
    },
    cadastrarProduto: {
        display: "flex",
        justifyContent: "flex-start",
        padding: "4px 0 10px",
    },
    botaoCadastrarProduto: {
        textTransform: "none",
        fontWeight: 600,
        color: "#2e7d32",
        border: "1px dashed #bcd8c2",
        borderRadius: 8,
        padding: "6px 14px",
        "&:hover": { backgroundColor: "rgba(76,175,80,0.06)", borderColor: "#4caf50" },
    },
    lista: {
        width: "100%",
    },
    dragIndicator: {
        cursor: "move",
    },
}))

const Categorias = memo(function Categorias({
    index,
    categoria,
    termoBusca,
    dragHandleProps,
    arrastando,
}) {
    const {
        setOpenproduto,
        setOpenCategoria,
        setCadastrar,
        setModalCategoria,
        setModalProduto,
        setCategoriaAtual,
        handleDuplicarCategoria,
        handleExcluirCategoria,
        setCategorias,
        handleIdsEditar,
        estabelecimento,
        categorias,
        persistirCategorias,
    } = useCardapioContext()

    // Edição inline do nome da categoria (igual ao produto). Salva no nível de
    // categoria (persistirCategorias) porque o "Salvar Alterações" não cobre
    // alterações só de categoria.
    const [editandoNome, setEditandoNome] = useState(false)
    const handleCommitNome = useCallback(
        async novoNome => {
            const nome = (novoNome ?? "").trim()
            if (!nome || nome === categoria.nome) return
            const novas = (categorias ?? []).map(cat =>
                cat.id === categoria.id ? { ...cat, nome } : cat
            )
            await persistirCategorias(novas)
        },
        [categoria.id, categoria.nome, categorias, persistirCategorias]
    )

    const draggingIndexRef = useRef(null)
    // Id do produto sendo arrastado (reativo, só para destacar visualmente o item).
    const [produtoArrastadoId, setProdutoArrastadoId] = useState(null)

    const perfil = useAppStore(s => s.configuracoes?.user?.perfil)
    const ehClienteFiel = perfil === "CLIENTEFIEL"

    const [anchorEl, setAnchorEl] = useState(null)
    const [openAcordion, setOpenAcordion] = useState(false)
    const [confirmarExcluir, setConfirmarExcluir] = useState(false)
    const [copiarAberto, setCopiarAberto] = useState(false)

    // Robusto: o backend pode mandar ativo como boolean false, 0 ou "false".
    const inativa =
        categoria?.ativo === false ||
        categoria?.ativo === 0 ||
        categoria?.ativo === "false"

    const classes = useStyles()

    const handleAccordionToggle = useCallback(() => {
        setCategoriaAtual(categoria)
        setOpenAcordion(prevOpen => !prevOpen)
    }, [categoria, setCategoriaAtual])

    const handleClickMoreItemDeMontagem = useCallback(e => {
        setAnchorEl(e.currentTarget)
        e.stopPropagation()
    }, [])

    const handleCloseMoreItemDeMontagem = useCallback(e => {
        setAnchorEl(null)
        e.stopPropagation()
    }, [])

    // Com busca ativa, a categoria abre automaticamente para mostrar o que casou.
    const termo = (termoBusca ?? "").toLowerCase()
    const aberto = openAcordion || !!termo

    const produtosOrdenados = useMemo(() => {
        if (!aberto) return []
        let p = (categoria?.produtos ?? []).filter(prod => !ehProdutoAdicionais(prod))
        // Esconde o produto especial "Adicionais" (passos comuns) da lista normal.
        // Com busca: mostra apenas os produtos que casam pelo próprio nome ou por
        // alguma opção de produto (o nome da categoria não é critério de busca).
        if (termo) {
            p = p.filter(prod => produtoCasaBusca(prod, termo))
        }
        return [...p].sort((a, b) => a.ordemExib - b.ordemExib)
    }, [categoria?.produtos, aberto, termo])

    // Reordena os produtos por arrastar. Ao mover, reatribui `ordemExib` na ordem
    // visível e marca cada produto como editado (persistido no "Salvar" global).
    // O produto oculto "Adicionais" é preservado no fim da categoria.
    const handleReordenarProduto = useCallback(
        overIndex => {
            const from = draggingIndexRef.current
            if (from === null || from === overIndex) return
            const arr = [...produtosOrdenados]
            const [movido] = arr.splice(from, 1)
            arr.splice(overIndex, 0, movido)
            const reordenados = arr.map((prod, idx) => ({ ...prod, ordemExib: idx }))
            reordenados.forEach(prod => handleIdsEditar(prod.id))
            const visiveisIds = new Set(reordenados.map(prod => prod.id))
            setCategorias(prevCats =>
                prevCats?.map(cat =>
                    cat.id === categoria.id
                        ? {
                              ...cat,
                              produtos: [
                                  ...reordenados,
                                  ...cat.produtos.filter(prod => !visiveisIds.has(prod.id)),
                              ],
                          }
                        : cat
                )
            )
            draggingIndexRef.current = overIndex
        },
        [produtosOrdenados, categoria.id, setCategorias, handleIdsEditar]
    )

    return (
        <div className={classes.container}>
            <Accordion
                className={`${classes.accordion} ${
                    arrastando ? classes.accordionArrastando : ""
                }`}
                expanded={aberto}
                onChange={handleAccordionToggle}
            >
                <AccordionSummary
                    className={`${classes.accordionSummary} ${
                        inativa ? classes.summaryInativo : ""
                    }`}
                    expandIcon={<ExpandMore />}
                >
                    <IconButton
                        {...dragHandleProps}
                        size="small"
                        title="Arraste para reordenar"
                        aria-label="Arraste para reordenar"
                        className={classes.dragHandle}
                        onClick={e => e.stopPropagation()}
                    >
                        <DragIndicator fontSize="small" />
                    </IconButton>
                    <Grid container alignItems="center" style={{ flex: 1, width: "auto" }}>
                        {/* Só o texto (largura do conteúdo) captura o clique de edição;
                            o espaço vazio à direita continua abrindo/fechando o accordion. */}
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{ minWidth: editandoNome ? 220 : 0 }}
                        >
                            <TextoEditavel
                                valorInicial={categoria.nome}
                                placeholder="Sem nome"
                                viewClassName={`${classes.tituloCategoria} ${
                                    inativa ? classes.tituloInativo : ""
                                }`}
                                termoDestaque={(termoBusca ?? "").toLowerCase()}
                                ativo={editandoNome}
                                onAtivar={() => setEditandoNome(true)}
                                onFechar={() => setEditandoNome(false)}
                                onCommit={handleCommitNome}
                            />
                        </div>
                        {inativa && (
                            <Chip
                                size="small"
                                icon={<Block style={{ color: "#DC4C64", fontSize: 16 }} />}
                                label="(inativo)"
                                className={classes.chipInativa}
                            />
                        )}
                    </Grid>
                    <IconButton onClick={handleClickMoreItemDeMontagem}>
                        <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMoreItemDeMontagem}
                        PaperProps={{
                            style: {
                                maxHeight: ITEM_HEIGHT * 4.5,
                                minWidth: "27ch",
                            },
                        }}
                    >
                        <MenuItem
                            onClick={e => {
                                setOpenCategoria(true)
                                setCadastrar(false)
                                setModalCategoria(categoria)
                                e.stopPropagation()
                            }}
                        >
                            Editar Categoria
                        </MenuItem>
                        <MenuItem
                            onClick={e => {
                                e.stopPropagation()
                                setCadastrar(true)
                                handleDuplicarCategoria(categoria.id)
                            }}
                        >
                            Duplicar Categoria
                        </MenuItem>
                        {ehClienteFiel && (
                            <MenuItem
                                onClick={e => {
                                    e.stopPropagation()
                                    setAnchorEl(null)
                                    setCopiarAberto(true)
                                }}
                            >
                                Copiar para outra loja
                            </MenuItem>
                        )}
                        <MenuItem
                            onClick={e => {
                                e.stopPropagation()
                                setAnchorEl(null)
                                setConfirmarExcluir(true)
                            }}
                        >
                            Excluir Categoria
                        </MenuItem>
                    </Menu>
                    <DialogConfirmacao
                        open={confirmarExcluir}
                        titulo="Excluir categoria"
                        mensagem="Tem certeza de que deseja excluir a categoria"
                        nomeItem={categoria.nome}
                        onCancelar={() => setConfirmarExcluir(false)}
                        onConfirmar={() => {
                            setConfirmarExcluir(false)
                            handleExcluirCategoria(categoria.id)
                        }}
                    />
                    {ehClienteFiel && (
                        <DialogCopiarCategoria
                            open={copiarAberto}
                            onClose={() => setCopiarAberto(false)}
                            categoria={categoria}
                            idEstabelecimentoAtual={estabelecimento?.id}
                        />
                    )}
                </AccordionSummary>
                {aberto && (
                    <AccordionDetails>
                        <List className={classes.lista} fullWidth>
                            <ListItem className={classes.cadastrarProduto} disableGutters>
                                <Button
                                    className={classes.botaoCadastrarProduto}
                                    startIcon={<Add />}
                                    onClick={() => {
                                        setCategoriaAtual(categoria)
                                        setOpenproduto(true)
                                        setCadastrar(true)
                                        setModalProduto(null)
                                    }}
                                >
                                    Cadastrar produto
                                </Button>
                            </ListItem>
                            {produtosOrdenados.map((produto, produtoIndex) => (
                                <ListItem
                                    key={`${produto.id}:${produto.versao ?? ""}`}
                                    disableGutters
                                    onDragOver={e => {
                                        e.preventDefault()
                                        handleReordenarProduto(produtoIndex)
                                    }}
                                    onDrop={() => {
                                        draggingIndexRef.current = null
                                        setProdutoArrastadoId(null)
                                    }}
                                    onDragEnd={() => {
                                        draggingIndexRef.current = null
                                        setProdutoArrastadoId(null)
                                    }}
                                >
                                    <ItemProduto
                                        conteudo={produto}
                                        categoria={categoria}
                                        termoBusca={termo}
                                        arrastando={produto.id === produtoArrastadoId}
                                        dragHandleProps={{
                                            draggable: true,
                                            onDragStart: () => {
                                                draggingIndexRef.current = produtoIndex
                                                setProdutoArrastadoId(produto.id)
                                            },
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                )}
            </Accordion>
        </div>
    )
})

export default Categorias
